import { useCallback } from 'react';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { UserSettingsAction, userSettingSelector } from '@cbridge-packages/user-settings';
import { SIDEBAR_COLLAPSED_SETTING_SUFFIX, SIDEBAR_WIDTH_SETTING_SUFFIX } from '../SidebarNavigationDrawer.constant';
import { clampDrawerWidth } from '../_helpers/clampDrawerWidth';

/**
 * Profile-persisted drawer state under `{settingsKey}.sidebar.collapsed` / `.width` (LB-4992).
 * Without a key nothing is read or written. The saved values are meant to seed local state once at
 * mount; later saves are write-through and do not feed back into the drawer.
 */
export const useSidebarSettings = (settingsKey?: string) => {
  const dispatch = useDispatch();
  const collapsedKey = settingsKey ? `${settingsKey}${SIDEBAR_COLLAPSED_SETTING_SUFFIX}` : '';
  const widthKey = settingsKey ? `${settingsKey}${SIDEBAR_WIDTH_SETTING_SUFFIX}` : '';
  const savedCollapsed = useSelector(userSettingSelector, { settingKey: collapsedKey });
  const savedWidth = useSelector(userSettingSelector, { settingKey: widthKey });

  const saveCollapsed = useCallback((collapsed: boolean) => {
    if (collapsedKey) {
      dispatch(UserSettingsAction.saveSetting({ key: collapsedKey, value: collapsed }));
    }
  }, [collapsedKey, dispatch]);

  const saveWidth = useCallback((width: number) => {
    if (widthKey) {
      dispatch(UserSettingsAction.saveSetting({ key: widthKey, value: width }));
    }
  }, [widthKey, dispatch]);

  return {
    savedCollapsed: typeof savedCollapsed === 'boolean' ? savedCollapsed : undefined,
    // A width saved on a wider screen may exceed the maximum of this one.
    savedWidth: typeof savedWidth === 'number' ? clampDrawerWidth(savedWidth) : undefined,
    saveCollapsed,
    saveWidth,
  };
};
